/**
 * @override
 * Shop The Look — Contentful entry with Image Hotspot Creator JSON and hotspotsContent references.
 * Fetches full entry by id when HotspotContent slides are linked so nested image/video/text resolve
 * (slim page queries often only return __typename + sys for collection items).
 */

import PropTypes from 'prop-types';

import {Box, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useShopTheLookById} from '@amblique/contentful/app/contentful/hooks/use-shop-the-look';
import {ShopTheLookBlock} from '@amblique/king-living/app/components/shop-the-look/shop-the-look-block';
import {useShopTheLookIsEmbedded} from '@amblique/king-living/app/contentful/models/shop-the-look-embed-context';
import {
  shopTheLookHasHotspotsContentCollection,
  shopTheLookNeedsFullHotspotFetch,
} from '@amblique/king-living/app/utils/shop-the-look-data';

/** Standalone outer gutters when HotspotContent is linked; productId-only JSON uses flush base. */
const STANDALONE_SHOP_THE_LOOK_VARIANT = 'standaloneContent';

export const ShopTheLook = ({
  component,
  isComplete = true,
  isFetching = false,
  variant: _parentChakraVariant,
  ...props
}) => {
  const id = component?.sys?.id;
  const needsHotspotFetch = shopTheLookNeedsFullHotspotFetch(component);
  const isEmbedded = useShopTheLookIsEmbedded();

  const {data: fullShopTheLook, isFetching: isFetchingHotspotDetail} = useShopTheLookById(id, {
    variant: 'full',
    queryOptions: {
      enabled: Boolean(id && needsHotspotFetch),
    },
  });

  const mergedComponent = fullShopTheLook ?? component;

  if (!mergedComponent?.imageUrl && !mergedComponent?.hotspots) {
    return null;
  }

  const hasHotspotsContent = shopTheLookHasHotspotsContentCollection(mergedComponent);
  const flushOuterChrome = isEmbedded || !hasHotspotsContent;

  const showSkeleton =
    isFetching ||
    !isComplete ||
    (needsHotspotFetch && isFetchingHotspotDetail && !fullShopTheLook);

  const blockVariant = flushOuterChrome ? undefined : STANDALONE_SHOP_THE_LOOK_VARIANT;

  if (showSkeleton) {
    return (
      <Box data-testid="shop-the-look-skeleton" py={flushOuterChrome ? 0 : 8} {...props}>
        <Skeleton borderRadius="base" height="320px" />
      </Box>
    );
  }

  return (
    <ShopTheLookBlock
      {...props}
      component={mergedComponent}
      isFetching={isFetching || isFetchingHotspotDetail}
      variant={blockVariant}
    />
  );
};

ShopTheLook.displayName = 'ShopTheLook';

ShopTheLook.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  variant: PropTypes.string,
};
